import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useDispatch } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Grid, Typography } from '@mui/material';
// import PhoneIcon from '@mui/icons-material/Phone';
import success from "../../../Assets/Icons/success.svg";
import { getActiveBrandConfig } from '../../../config/environment.config';
import useTailwindBrand from '../../../Theme/useTailwindBrand';
import ButtonField from '../../../Components/Ui/ButtonField';
import FormInput from '../../../Components/Ui/FormInput';
import DialogLayout from '../../../Components/DialogLayout';
import Settings from '../../../Components/Settings';
import { useProfilContext } from '../../../Contexts/ProfilContexte';
import Teachr_Repository from '../../../Repositories/Teachr_Repository';
import { userTeachrSetAccountAction } from '../../../Redux/Actions/UserActions';
import { auth } from '../../../Services/FirebaseService';
import { AppDispatch } from '../../../Redux/store';
import type { DataDilogLayout } from '../../../Objects/interfaces/AppInterface';

const LastCompletProfil: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [user] = useAuthState(auth);
    const { classes, common } = useTailwindBrand();
    const { teachr } = useProfilContext();

    const [openValidateDialog, setOpenValidateDialog] = useState(false);
    const [code, setCode] = useState('');
    const [load, setLoad] = useState(false);
    const [validationError, setValidationError] = useState<string | undefined>(undefined);
    // const [validatedSuccess, setValidatedSuccess] = useState(false);

    const isAlreadyValidated = !!(teachr?.isValidated && teachr?.isAE && teachr.signSap);

    useEffect(() => {
        if (!teachr) return;
        if (!teachr.proofMandat) {
            navigate(`/${getActiveBrandConfig()}/profile/complete`);
            return;
        }
        if (teachr.proofMandat && teachr.address && !isAlreadyValidated) {
            setOpenValidateDialog(true);
        }
    }, [teachr, isAlreadyValidated, navigate]);

    const validateWithCode = useCallback(
        (validationCode: string) => {
            if (!teachr?.id) return Promise.reject(new Error('No teachr ID'));
            return Teachr_Repository.teachrValidateAccountWithCode(teachr.id, validationCode);
        },
        [teachr?.id]
    );

    const handleCloseValidateDialog = useCallback(() => {
        setOpenValidateDialog(false);
        setCode('');
        setValidationError(undefined);
    }, []);

    const handleSubmitValidation = useCallback(() => {
        const trimmedCode = code?.trim();
        if (!trimmedCode) {
            setValidationError('Veuillez renseigner le code de validation.');
            return;
        }
        setValidationError(undefined);
        setLoad(true);
        validateWithCode(trimmedCode)
            .then((response: any) => {
                const result = Array.isArray(response) ? response[0] : response;
                if (result === 'BAD_CODE') {
                    setValidationError('Code invalide. Veuillez réessayer.');
                    setLoad(false);
                    return;
                }
                return Teachr_Repository.getCurrentTeachr().then((updatedTeachr) => {
                    const serializedTeachr = JSON.parse(JSON.stringify(updatedTeachr));
                    dispatch(userTeachrSetAccountAction(serializedTeachr));
                    dispatch({
                        type: 'SET_TEACHR_INFO',
                        payload: {
                            teachr: serializedTeachr,
                            authorization: user ? `teachr ${user.getIdToken(false)}` : undefined,
                        },
                    } as any);
                    setOpenValidateDialog(false);
                    // setValidatedSuccess(true);
                    setCode('');
                });
            })
            .catch(() => {
                setValidationError('Une erreur est survenue. Veuillez réessayer.');
            })
            .finally(() => {
                setLoad(false);
            });
    }, [code, validateWithCode, dispatch, user]);

    const dataDialogValidate: DataDilogLayout = {
        title: 'Valider mon compte',
        text: (
            <p className={common.body}>
                Merci de bien vouloir renseigner le code de validation de votre compte qui vous a été transmis par l'un de nos administrateurs.
            </p>
        ),
        open: openValidateDialog,
        valid: 'Valider',
        load,
        handleClose: handleCloseValidateDialog,
        handleSubmit: handleSubmitValidation,
        handleCloseIcon: handleCloseValidateDialog,
        children: (
            <div className="mt-4">
                <FormInput
                    label="Code de validation"
                    type="text"
                    placeholder="Ex : OKAESAP-YD009900"
                    value={code}
                    onChange={(e) => {
                        setCode(e.target.value);
                        setValidationError(undefined);
                    }}
                    error={validationError}
                />
            </div>
        ),
    };

    const procuration = teachr?.procuration;

    const docs: { label: string; url: string }[] = [];
    if (teachr?.proofMandat?.contentUrl) {
        docs.push({ label: 'Conditions de services signées', url: teachr.proofMandat.contentUrl });
    }
if (procuration === '20' && teachr?.proofSignSap?.contentUrl) {
        docs.push({ label: 'Procuration SAP', url: teachr.proofSignSap.contentUrl });
    }
    if (procuration === '30') {
        if (teachr?.proofIsAE?.contentUrl) {
            docs.push({ label: 'Procuration AE+SAP', url: teachr.proofIsAE.contentUrl });
        }
    }

    const docsSection = docs.length > 0 && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <Typography variant="subtitle2" fontWeight="700" className={`mb-3 ${classes.fontFamily}`}>
                Vos documents
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {docs.map(({ label, url }) => (
                    <a key={url} href={url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
                        <ButtonField
                            label={label}
                            variant="outlined"
                            className="font-bold w-full"
                            startIcon={<DownloadIcon />}
                        />
                    </a>
                ))}
            </Box>
        </div>
    );

    if (isAlreadyValidated) {
        return (
            <Settings title="Félicitations !" subtitle="Votre compte est désormais validé" height="100%">
                <Grid item xs={12} sm={12} md={12} lg={12}>
                    <div className="space-y-6">
                        <div className="flex flex-col items-center gap-4 py-8">
                            <div
                                className="relative flex items-center justify-center"
                                style={{
                                    background: "radial-gradient(50% 50.00% at 50% 50.00%, #B9D0FF 0%, rgba(242, 245, 251, 0.00) 100%)",
                                    width: '200px',
                                    height: '200px',
                                    borderRadius: '50%'
                                }}
                            >
                                <img src={success} alt="Succès" className="w-32 h-32 object-contain" />
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircleIcon className="w-8 h-8 text-green-600" />
                                <h3 className={`text-2xl font-extrabold ${classes.primaryText} ${classes.fontFamily}`}>
                                    COMPTE VALIDÉ
                                </h3>
                            </div>
                        </div>

                        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className={`${common.body} font-bold text-center ${classes.fontFamily}`}>
                                Félicitations, votre compte est désormais validé 🥳<br /><br />
                                Vous pouvez dès maintenant accéder à votre Tableau de bord et profiter de l'avance immédiate.
                            </p>
                        </div>

                        {docsSection}

                        <div className="flex justify-end pt-4">
                            <ButtonField
                                label="Tableau de bord"
                                variant="contained"
                                className="w-full font-bold lg:w-auto"
                                onClick={() => navigate(`/${getActiveBrandConfig()}/tableau-de-bord`)}
                            />
                        </div>
                    </div>
                </Grid>
            </Settings>
        );
    }

    return (
        <>
            <DialogLayout {...dataDialogValidate} />

            <Settings title="Félicitations !" subtitle="Votre inscription est en cours de traitement" height="100%">
                <Grid item xs={12} sm={12} md={12} lg={12}>
                    <div className="space-y-6">
                        <div className="flex flex-col items-center gap-4 py-8">
                            <div
                                className="relative flex items-center justify-center"
                                style={{
                                    background: "radial-gradient(50% 50.00% at 50% 50.00%, #B9D0FF 0%, rgba(242, 245, 251, 0.00) 100%)",
                                    width: '200px',
                                    height: '200px',
                                    borderRadius: '50%'
                                }}
                            >
                                <img src={success} alt="Succès" className="w-32 h-32 object-contain" />
                            </div>
                            <div className="flex items-center gap-2">
                                <CheckCircleIcon className="w-8 h-8 text-green-600" />
                                <h3 className={`text-2xl font-extrabold ${classes.primaryText} ${classes.fontFamily}`}>
                                    FÉLICITATIONS
                                </h3>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className={`${common.body} font-bold text-center ${classes.fontFamily}`}>
                                    Nous avons reçu les deux documents que vous nous avez envoyés.
                                </p>
                            </div>

                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                <p className={`${common.body} font-bold text-center ${classes.fontFamily}`}>
                                    Validez votre compte avec le code transmis par notre équipe pour accéder à votre Tableau de bord.
                                </p>
                            </div>

                            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className={`${common.body} font-semibold text-center mb-3 ${classes.fontFamily}`}>
                                    Passé ce délai, si vous n'avez pas de retour de notre part, vous pouvez nous contacter :
                                </p>
                                <div className="flex flex-col md:flex-row items-center justify-center gap-4 mt-4">
                                    <div className="flex items-center gap-2">
                                        <EmailIcon className="w-5 h-5 text-amber-700" />
                                        <a
                                            href={`mailto:${process.env.REACT_APP_MAIL_SUPPORT}`}
                                            className={`font-bold ${classes.primaryText} hover:underline ${classes.fontFamily}`}
                                        >
                                            {process.env.REACT_APP_MAIL_SUPPORT}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {docsSection}

                        <div className="flex justify-end pt-4">
                            <ButtonField
                                label="Valider mon compte"
                                variant="contained"
                                className="w-full font-bold lg:w-auto"
                                onClick={() => setOpenValidateDialog(true)}
                            />
                        </div>
                    </div>
                </Grid>
            </Settings>
        </>
    );
};

export default LastCompletProfil;
